import type { MouseEvent } from "react"

import { usePrefersReducedMotion } from "@/hooks/usePrefersReducedMotion"

/**
 * Height of the sticky product bar when it sits over `target`: on narrow
 * screens the bar pins across the top of the page, so a section scrolled to
 * the very top would start underneath it. On desktop the bar is a side
 * column beside the sections, so no offset is needed.
 */
function stickyBarOffset(target: HTMLElement) {
  const bar = document.querySelector<HTMLElement>("[data-product-bar]")
  if (!bar) return 0
  const barRect = bar.getBoundingClientRect()
  const targetRect = target.getBoundingClientRect()
  const overlaps = barRect.left < targetRect.right && barRect.right > targetRect.left
  return overlaps ? barRect.height : 0
}

/**
 * Click handler for in-page links to a product section (`#code-hosting` …):
 * scrolls there smoothly, instantly under reduced motion, clear of the sticky
 * product bar, and keeps the hash in the URL without the browser's jump.
 * Shared by the sticky menu and the hero cards.
 */
export function useScrollToProduct() {
  const prefersReducedMotion = usePrefersReducedMotion()

  return (event: MouseEvent<HTMLAnchorElement>, id: string) => {
    // Modified and non-primary clicks open the link the browser's way (new tab…).
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return
    }
    const target = document.getElementById(id)
    if (!target) return
    event.preventDefault()
    // Set as the section's scroll margin rather than subtracted from a scroll
    // position: the router also scrolls to the new hash and honours it too.
    target.style.scrollMarginTop = `${stickyBarOffset(target)}px`
    target.scrollIntoView({ behavior: prefersReducedMotion ? "instant" : "smooth", block: "start" })
    window.history.replaceState(window.history.state, "", `#${id}`)
  }
}
